<template>
    <span
        v-click-outside="closePopover"
        class="token-wrapper"
        :class="{
            'start-selected' : isStartSelected,
            'end-selected'   : isEndSelected,
        }"
    >
        <span
            v-for="(bracket, i) in startRange"
            class="bracket"
            :class="bracketGroup(bracket.entity)"
            :key="`start-${i}`"
        >
            {
        </span>

        <span
            v-show="isLeftHover"
            class="bracket"
        >
            {
        </span>

        <span
            ref="token"
            class="token"
            @mouseenter="mouseenterHandler"
            @mouseleave="isHovered = false"
            @click="selectToken"
        >
            {{ token.value }}
        </span>

        <span
            v-show="isRightHover"
            class="bracket"
        >
            }
        </span>

        <EntityLabel
            v-for="(bracket, i) in endRange"
            :class="bracketGroup(bracket.entity)"
            :bracket="bracket"
            :key="`end-${i}`"
            :entities="entities"
            @open="closePopover"
            @selectRole="handleRoleSelection"
            @remove-entity="removeEntity"
        />

        <span
            v-if="token.spaced"
            class="space"
        >{{' '}}</span>

        <or-popover
            ref="popover"
            open-on="focus"
            position="top right"
            trigger="token"
        >
            <div v-if="entitiesGroups.length !== 0">
                <div
                    class="entity-group"
                    v-for="entityGroup in entitiesGroups"
                    :key="entityGroup.groupName"
                >
                    <div class="entity-group--name">
                        {{entityGroup.groupName}}
                    </div>

                    <ml-entity-selector
                        v-for="entity in entityGroup.entities"
                        :key="entity.id"
                        :ml-entity="entity"
                        :head-node="true"
                        @select="handleMlEntitySelection"
                    />
                </div>
            </div>
            <div
                v-else
                class="question-popover placeholder center"
            >
              {{ $t('common.utterance.entity-token.entity-token.noMachineLearningEntity') }}
            </div>
        </or-popover>
    </span>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import directives from '../directives'
import EntityLabel from './entity_label'
import MlEntitySelector from './ml-entity-selector'
import { ENTITY_TYPE } from '@/components/model/nlu-model/sidebar/entities/entity-types'

export default {
  components: {
    EntityLabel,
    MlEntitySelector
  },

  directives,

  props: {
    token: Object,
    index: Number,

    entities: {
      type: Array,
      default: () => []
    },

    entityPredictions: {
      type: Array,
      default: () => []
    },

    selectedToken: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      isHovered: false
    }
  },

  computed: {
    ...mapState('knowledgebase/entities', ['machineLearningEntities']),
    ...mapGetters('knowledgebase', ['getCurrent']),

    modelExtensions () {
      return _.get(this.getCurrent, 'extensions')
    },

    mlEntities () {
      const addParentToChildEntity = entity =>
        entity.children && entity.children.map(childEntity => {
          childEntity = { ...childEntity }
          childEntity.parent = entity
          childEntity.children = addParentToChildEntity(childEntity)
          return childEntity
        })

      return this.machineLearningEntities.value.map(entity => {
        entity = { ...entity }
        entity.children = addParentToChildEntity(entity)
        return entity
      })
    },

    entitiesGroups () {
      const localMLEntities = this.localEntities.filter(
        entity => entity.type === ENTITY_TYPE.machineLearning && entity.isConfirmed
      )

      const getNotCoveredEntities = (groupName, selectedEntities, allEntities) => {
        const groups = []
        const coveredMLEntities = []

        selectedEntities.forEach(entity => {
          const doesEntityCoversSelection =
            entity.start <= this.localSelectedToken.start &&
            entity.end >= this.localSelectedToken.end

          if (doesEntityCoversSelection) {
            coveredMLEntities.push(entity)
          }
        })

        const notCoveredMLEntities = allEntities
          .filter(mlEntity => !coveredMLEntities.some(entity => entity.entity === mlEntity.name))

        if (notCoveredMLEntities.length !== 0) {
          groups.push({
            groupName,
            entities: notCoveredMLEntities
          })
        }

        coveredMLEntities.forEach(entity => {
          const possibleEntityOptions = allEntities.find(e => e.name === entity.entity)
          // Can happen if we (a) delete entity (b) rename it (in ML editor)
          if (!possibleEntityOptions) {
            console.debug('Looks like entity does not exists anymore:', entity, allEntities)
            return
          }
          if (!entity.children || entity.children.length === 0) {
            groups.push({
              groupName: entity.entity,
              entities: possibleEntityOptions.children
            })
          }
          groups.push(...getNotCoveredEntities(entity.entity, entity.children || [], possibleEntityOptions.children || []))
        })

        return groups
      }

      return _.uniqBy(
        getNotCoveredEntities(
          this.$t('common.utterance.entity-token.entity-token.mlEntities'),
          localMLEntities.filter(entity => !entity.virtual).map(({ virtual, ...rest }) => rest),
          this.mlEntities
        ),
        'groupName'
      )
    },

    isStartSelected () {
      return this.localSelectedToken.start === this.index
    },

    isLeftHover () {
      if (this.isHovered) {
        return this.index < this.localSelectedToken.start ||
                    this.localSelectedToken.end === null
      }

      return false
    },

    isRightHover () {
      if (this.isHovered) {
        return this.index > this.localSelectedToken.end ||
                    this.localSelectedToken.start === null
      }

      return false
    },

    isEndSelected () {
      return this.localSelectedToken.end === this.index
    },

    localEntities () {
      const entitiesArr = []

      function addTypeToChildren (entity, isConfirmed) {
        const { type } = entity
        const stack = [{ ...entity, virtual: false }]

        while (stack.length) {
          const el = stack.pop()
          entitiesArr.push({
            virtual: true,
            ...el,
            isConfirmed,
            type
          })
          if (el.children) {
            stack.push(...el.children)
          }
        }
      }

      this.$props.entities.forEach(entity => addTypeToChildren(entity, true))
      this.entityPredictions.forEach(entity => addTypeToChildren(entity, false))

      return entitiesArr
    },

    startRange () {
      return _
        .chain(this.localEntities)
        .filter(['start', this.index])
        .sortBy(entity => {
          if (entity.type !== ENTITY_TYPE.machineLearning) {
            return -1
          }
          let depth = 0
          while (entity.children && entity.children.length !== 0) {
            entity = entity.children[0]
            depth++
          }
          return depth
        })
        .sortBy(({ start, end }) => (end - start))
        .reverse()
        .value()
    },

    endRange () {
      return _
        .chain(this.localEntities)
        .filter(['end', this.index])
        .sortBy(entity => {
          if (entity.type !== ENTITY_TYPE.machineLearning) {
            return -1
          }
          let depth = 0
          while (entity.children && entity.children.length !== 0) {
            entity = entity.children[0]
            depth++
          }
          return depth
        })
        .sortBy(({ start, end }) => (end - start))
        .value()
    },

    localSelectedToken: {
      get () {
        return this.selectedToken
      },
      set (value) {
        this.$emit('update:selectedToken', value)
      }
    }
  },

  methods: {
    mouseenterHandler () {
      this.isHovered = !_.isEmpty(this.modelExtensions)
    },

    handleRoleSelection (entity, role) {
      this.$emit('set-entity-role', entity, role)
    },

    handleMlEntitySelection (entity) {
      let entityToken = {
        entity: entity.name,
        children: []
      }
      while (entity.parent) {
        entity = entity.parent
        entityToken = {
          entity: entity.name,
          children: [entityToken]
        }
      }

      entityToken.role = undefined
      entityToken.value = this.token.value

      this.$emit('add-entity', entityToken)
      this.$refs.popover.close()
    },

    addEntity (entity) {
      this.$emit('add-entity', entity)
      this.$refs.popover.close()
    },

    removeEntity (entity) {
      this.$emit('remove-entity', entity)
    },

    bracketGroup (entity) {
      const groupList = [
        'green',
        'red',
        'yellow',
        'lightcoral',
        'yellowgreen',
        'hotpink',
        'sandybrown',
        'chocolate'
      ]

      const group = [...entity].reduce((count, el) => (count + el.charCodeAt()), 0) % groupList.length
      return groupList[group]
    },

    closePopover () {
      if (this.$refs.popover) {
        this.$refs.popover.close()
      }
    },

    selectToken () {
      if (_.isEmpty(this.getCurrent.extensions)) {
        return
      }

      if (
        this.localSelectedToken.end === this.index &&
                this.localSelectedToken.start === this.index
      ) {
        this.localSelectedToken.start = null
        this.localSelectedToken.end = null
        this.$refs.popover.close()
      } else {
        if (this.localSelectedToken.start === null) {
          this.localSelectedToken.start = this.index
          this.localSelectedToken.end = this.index
        } else if (this.localSelectedToken.start > this.index) {
          this.localSelectedToken.start = this.index
        } else if (this.localSelectedToken.end === this.index) {
          this.localSelectedToken.start = this.index
        } else {
          this.localSelectedToken.end = this.index
        }

        this.$refs.popover.open()
      }
    }
  },

  watch: {
    selectedToken: {
      handler (value) {
        if (value.start === null) {
          this.$refs.popover.close()
        }
      }
    }
  }
}
</script>

<style lang="scss">
    .question-popover{
        display: flex;
        flex-direction: column;
        height: 100px;
        width: 100px;
        overflow-y: auto;
        font-size: 14px;
        cursor: pointer;

        &.center{
            text-align: center;
            align-items: center;
            justify-content: center;
        }
        &.placeholder{
            text-align: left;
            color: #91969D;
            &.center{
                text-align: center;
                align-items: center;
                justify-content: center;
            }
        }

        .highlight{
            color: #409fed;
            text-decoration: underline;
            text-decoration-color: #409fed;
        }

        .item{
            transition: 0.1s all ease-in-out;
            padding: 3px 5px;

            &:hover{
                color: white;
                background-color: #409fed;
            }
        }
    }
</style>

<style lang="scss" scoped>
    .entity-group {
        max-height: 120px;
        overflow: scroll;

        &--name {
            padding: 8px;
            color: rgb(145, 150, 157);
            font-size: 14px;
            font-weight: normal;
        }
    }

    .bracket{
        line-height: 1.8;
        font-size: 14px;
        font-weight: bold;
        color: $or-black-blue;

        .bracket-label{
            color: white;
            border-radius: 3px;
            margin: 0 3px;
            padding: 2px 4px;
            font-size: 14px;
            font-weight: normal;
        }

        &.green{
            color: darkgreen;
            .bracket-label{
                color: white;
                background-color: darkgreen;
            }
        }

        &.red{
            color: darkred;
            .bracket-label{
                color: white;
                background-color: darkred;
            }
        }

        &.yellow{
            color: darkorange;
            .bracket-label{
                color: white;
                background-color: darkorange;
            }
        }

        &.lightcoral{
            color: lightcoral;
            .bracket-label{
                color: white;
                background-color: lightcoral;
            }
        }

        &.yellowgreen{
            color: yellowgreen;
            .bracket-label{
                color: white;
                background-color: yellowgreen;
            }
        }

        &.hotpink{
            color: hotpink;
            .bracket-label{
                color: white;
                background-color: hotpink;
            }
        }

        &.sandybrown{
            color: sandybrown;
            .bracket-label{
                color: white;
                background-color: sandybrown;
            }
        }

        &.chocolate{
            color: chocolate;
            .bracket-label{
                color: white;
                background-color: chocolate;
            }
        }
    }

    .start-selected, .end-selected{
        .token::before, .token::after{
            color:#409fed;
            font-size: 14px;
            font-weight: bold;
        }
    }

    .start-selected .token:before{
        content: "{"
    }

    .end-selected .token:after{
        content: "}"
    }
</style>

<style lang="scss">
    .drop-content {
        padding: 10px;
    }
</style>
